"use client";

import type React from "react";
import { Search, TrendingUp, BarChart2, Brain, Globe, Shield } from "@/components/icons";
import { STATS } from "@/lib/constants";

export interface UseCase {
  id: string;
  label: string;
  icon: React.ComponentType<{ size?: number; className?: string; style?: React.CSSProperties }>;
  headline: string;
  description: string;
  benefits: string[];
  products: string[];
  href?: string;
}

export const useCases: UseCase[] = [
  {
    id: "scraping",
    label: "Web Scraping",
    icon: Search,
    headline: "Scrape any public website reliably at scale.",
    description:
      "Our proxy network of rotating residential and mobile IPs comes from real users, so your data pipelines run smoothly and reliably across every job.",
    benefits: [
      "99.5%+ success rate across all major websites",
      "Auto-rotation with customizable session durations",
      "Support for JavaScript-heavy SPAs and dynamic content",
      "Playwright, Puppeteer, Scrapy, and Selenium compatible",
    ],
    products: ["Mobile Proxies", "Residential Proxies"],
    href: "/use-cases/web-scraping",
  },
  {
    id: "price",
    label: "Price Monitoring",
    icon: TrendingUp,
    headline: "Track competitor prices in real time.",
    description:
      "Monitor pricing across Amazon, eBay, Walmart, and thousands of e-commerce platforms with reliable access at scale.",
    benefits: [
      `Collect data reliably at scale across millions of IPs`,
      "Location-specific pricing data from any region",
      "Schedule scrapes at custom intervals",
      "Pre-built integrations with popular monitoring stacks",
    ],
    products: ["Residential Proxies", "Mobile Proxies"],
    href: "/use-cases/price-monitoring",
  },
  {
    id: "seo",
    label: "SEO Monitoring",
    icon: BarChart2,
    headline: "Track SERP rankings from any location.",
    description:
      "Check major search engines for real keyword rankings across cities and countries, with the exact results your target audience sees.",
    benefits: [
      "City and country-level SERP accuracy",
      "Requests from real IPs that blend in with everyday traffic",
      "Support for Google, Bing, Yahoo, Yandex, and Baidu",
      "Monitor AI Overviews and rich feature snippets",
    ],
    products: ["Residential Proxies"],
    href: "/use-cases/seo-monitoring",
  },
  {
    id: "ai",
    label: "AI & LLM Training",
    icon: Brain,
    headline: "Fuel your AI models with fresh web data.",
    description:
      "Collect geo-diverse training data from across the web reliably at scale. More time building models, less time managing infrastructure.",
    benefits: [
      `${STATS.ips} IPs for diverse, unbiased dataset collection`,
      `Geo-targeted crawling across ${STATS.countries} countries`,
      "High-throughput crawling for bulk data collection.",
      "Residential & mobile IPs that mirror real user traffic",
    ],
    products: ["Residential Proxies", "Mobile Proxies"],
    href: "/use-cases/ai-llm-training/",
  },
  {
    id: "market",
    label: "Market Research",
    icon: Globe,
    headline: "Understand markets from any geography.",
    description:
      "Access region-specific content, local ads, and localized data with geo-targeted proxies. Research global markets exactly as local users experience them.",
    benefits: [
      `${STATS.countries} country targeting for precise geo accuracy`,
      "City and ISP-level geolocation",
      "Consistent IPs for longitudinal research studies",
      "Compatible with survey platforms and data pipelines",
    ],
    products: ["Residential Proxies", "Mobile Proxies"],
    href: "/use-cases/market-research/",
  },
  {
    id: "brand",
    label: "Brand Protection",
    icon: Shield,
    headline: "Monitor your brand across the web.",
    description:
      "Detect counterfeit listings, unauthorized sellers, and intellectual property violations across global marketplaces. Rotate across multiple IPs to pull accurate, uncached results from every market you monitor.",
    benefits: [
      "Anonymous monitoring from any location",
      "Detect geo-restricted violations in local markets",
      "Monitor ads for brand misuse and compliance",
      "Integrate with threat intelligence platforms",
    ],
    products: ["Residential Proxies", "Mobile Proxies"],
    href: "/use-cases/brand-protection-monitoring/",
  },
];
